import { NavLink, useMatch } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/Badge';
import { BADGE_LABEL } from './constants';
import type { SidebarBadge } from './index';

type Props = {
  to?: string;
  label: string;
  end?: boolean;
  badge?: SidebarBadge;
  // A design page that isn't available yet: rendered as greyed, non-interactive text.
  disabled?: boolean;
  onNavigate?: () => void;
};

const SUBITEM_BASE = 'flex h-7 w-full items-center rounded-md pl-2 pr-3 text-xs';

function SubItemBadge({ badge }: { badge?: SidebarBadge }) {
  if (!badge) return null;
  return (
    <Badge variant={badge} className="ml-1 shrink-0">
      {BADGE_LABEL[badge]}
    </Badge>
  );
}

function LinkSubItem({
  to,
  label,
  end,
  badge,
  onNavigate,
}: {
  to: string;
  label: string;
  end?: boolean;
  badge?: SidebarBadge;
  onNavigate?: () => void;
}) {
  const isActive = useMatch({ path: to, end: end ?? false }) != null;

  return (
    <NavLink
      to={to}
      end={end}
      data-testid="nav-subitem"
      onClick={onNavigate}
      className={cn(
        SUBITEM_BASE,
        'transition-colors',
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent-pressed'
          : 'text-sidebar-foreground hover:bg-sidebar-neutral-hover',
      )}
    >
      <span className="flex-1 truncate text-left">{label}</span>
      <SubItemBadge badge={badge} />
    </NavLink>
  );
}

export function SidebarSubItem({ to, label, end, badge, disabled, onNavigate }: Props) {
  if (disabled || !to) {
    return (
      <span
        aria-disabled="true"
        data-testid="nav-subitem-disabled"
        className={cn(SUBITEM_BASE, 'cursor-default text-[var(--color-sidebar-item-disabled)]')}
      >
        <span className="flex-1 truncate text-left">{label}</span>
        <SubItemBadge badge={badge} />
      </span>
    );
  }

  return <LinkSubItem to={to} label={label} end={end} badge={badge} onNavigate={onNavigate} />;
}
